"use client";

import React from "react";
import { Briefcase, Wrench, Sparkles, ConciergeBell, CircleCheck } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const roles = [{
  id: 1,
  title: "Traditional Production",
  description: "The legacy method: expensive, slow, and requiring massive human crews for every single social media asset.",
  icon: "Briefcase",
  benefit1: "$15,000+ per commercial",
  benefit2: "3-6 week turnaround time",
  benefit3: "Physical set & crew logistics"
}, {
  id: 2,
  title: "AI Commercials",
  description: "The neural revolution: hyper-realistic social ads generated at the speed of light for a fraction of the cost.",
  icon: "Sparkles",
  benefit1: "90% lower production costs",
  benefit2: "24-hour delivery window",
  benefit3: "Infinite digital environments"
}, {
  id: 3,
  title: "10x Iteration Speed",
  description: "Don't just launch one ad. Launch fifty. Test hooks, backgrounds, and actors without re-shooting.",
  icon: "Wrench",
  benefit1: "A/B test visual hooks",
  benefit2: "Instant platform resizing",
  benefit3: "Dynamic lighting changes"
}, {
  id: 4,
  title: "Neural Quality",
  description: "Cinematic 4K resolution powered by the world's most advanced generative video and audio models.",
  icon: "ConciergeBell",
  benefit1: "Studio-grade color grading",
  benefit2: "AI-powered voiceovers",
  benefit3: "Perfect social-first aspect ratios"
}];
const iconMap: Record<string, React.ElementType> = {
  Briefcase,
  Wrench,
  Sparkles,
  ConciergeBell
};
export default function TailoredForEveryDepartment() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224567" id="why-ai-wins" className="bg-background text-foreground py-24 md:py-32 px-4 overflow-hidden relative">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--primary)_1px,transparent_1px),linear-gradient(to_bottom,var(--primary)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-[0.03] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 md:mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center px-3 py-1 mb-6 text-sm font-default font-medium rounded-lg bg-primary/10 text-primary border border-primary/20 tracking-wider uppercase">
            The Production Revolution
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-7xl font-bold tracking-tighter mb-6 text-foreground uppercase">
            Why AI Wins
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed">
            Traditional video production is a relic of the past. We leverage neural rendering to deliver cinematic commercials at 10x the speed and 1/10th the cost.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 transition-all duration-1000 delay-200 ease-out ${isGridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {roles.map((role, i) => {
          const IconComponent = iconMap[role.icon];
          const isTraditional = i === 0;
          return <Card key={role.id} data-index={i} className={`group backdrop-blur-xl transition-all duration-500 shadow-lg hover:shadow-2xl overflow-hidden flex flex-col h-full ${isTraditional ? "bg-muted/5 border-muted/20 grayscale opacity-80" : "bg-card/40 border-primary/30 hover:border-primary/60 hover:bg-card/60 shadow-primary/5"}`}>
                <CardHeader className="pb-4">
                  <div className={`mb-6 inline-flex p-4 rounded-2xl transition-transform duration-500 ease-out shadow-inner border ${isTraditional ? "bg-muted/10 text-muted-foreground border-muted/10" : "bg-primary/10 text-primary group-hover:-translate-y-2 border-primary/10"}`}>
                    {IconComponent && <IconComponent className="size-8" />}
                  </div>
                  <CardTitle className="font-heading text-2xl md:text-3xl text-foreground font-bold tracking-tight">
                    {role.title}
                  </CardTitle>
                  <CardDescription className="text-base mt-2 text-muted-foreground font-default leading-relaxed">
                    {role.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="mt-auto pt-4 border-t border-border/10">
                  <ul className="space-y-3">
                    {[role.benefit1, role.benefit2, role.benefit3].map((benefit, index) => <li key={index} data-index={index} className="flex items-start gap-3">
                        <CircleCheck className={`size-5 shrink-0 mt-0.5 opacity-80 ${isTraditional ? "text-muted-foreground" : "text-primary"}`} />
                        <span className={`font-default text-sm md:text-base ${isTraditional ? "text-muted-foreground/70 line-through" : "text-foreground"}`}>
                          {benefit}
                        </span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}